import type { Maybe } from "@/domains/common/types";
import type { Uuid } from "@/domains/global/identifiers";
import { AssistantTaskObservable } from "@/store/assistant/AssistantTaskObservable";
import type { AssistantTaskIndexData, AssistantTaskModelData } from "@/store/assistant/types";
import type { AppStore } from "@/store/AppStore";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { makeObservable, override } from "mobx";

export class AppStoreAssistantTasksStore extends BaseSyncModelStore<
  AssistantTaskObservable,
  AssistantTaskModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.AssistantTask, ...injectedDeps });
    makeObservable<this, "createSyncModel">(this, {
      createSyncModel: false,
      computeIndexes: override,
      getAssistantTaskObservableById: false,
    });
  }

  createSyncModel(data: SyncUpdateValue<AssistantTaskModelData>): AssistantTaskObservable {
    return new AssistantTaskObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<AssistantTaskModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<AssistantTaskModelData>[];
  }): AssistantTaskIndexData {
    const syncUpdateValue =
      (optimisticUpdates.at(-1)?.value as SyncUpdateValue<AssistantTaskModelData> | undefined) ||
      remoteData;
    const modelData = syncUpdateValue?.model_data;

    return {
      assistant_id: modelData?.assistant_id ?? "",
      project_id: modelData?.project_id ?? "",
      status: modelData?.status ?? "",
      due_at: modelData?.due_at ?? "",
      created_at: modelData?.created_at ?? "",
      updated_at: modelData?.updated_at ?? "",
    };
  }

  getAssistantTaskObservableById({
    assistantTaskId,
  }: {
    assistantTaskId: Uuid;
  }): AssistantTaskObservable | undefined {
    return this.get(assistantTaskId);
  }
}
